import { TOKEN_KEY } from "@selector/shared/settings";
import { nanoid } from "nanoid";
import { type AnyAction, type Store } from "redux";
import { storage } from "widgets";

import Backoff from "@/app-utils/backoff.ts";
import {
  type AllSubscriptions,
  type ResponseData,
  type ResponseDataEvent,
  type SubscriptionData,
  SubscriptionMessageTypeEnum,
  type UnSubscriptionData,
} from "@/modules/notifications/utils/types.ts";
import { connectedWebsocket, disconnectedWebsocket, reconnectedWebsocket } from "@/redux/modules/websocket.ts";

export interface Subscription<S extends AllSubscriptions, E = ResponseDataEvent<S>> {
  subscription: S;
  onMessage: (message: E, subId: string) => void;
  onError?: (error: Error) => void;
}

type Subscriptions = Record<string, Subscription<AllSubscriptions>>;

export interface ClientOptions {
  timeout: number;
  reconnect: boolean;
  reconnectionAttempts: number;
}

export default class WebSocketClient {
  #client?: WebSocket;
  #subscriptions: Subscriptions = {};
  #url: string;
  #subscriptionTimeout: number;
  #waitingSubscriptions: Record<string, boolean> = {};
  #unsentMessagesQueue: (SubscriptionData<AllSubscriptions> | UnSubscriptionData)[] = [];
  #reconnect: boolean;
  #reconnecting = false;
  #reconnectionAttempts: number;
  #reconnectSubscriptions: Subscriptions = {};
  #backoff: Backoff = new Backoff({ jitter: 0.5 });
  #store?: Store;

  constructor(url: string, options: ClientOptions) {
    this.#url = url;
    this.#subscriptionTimeout = options.timeout;
    this.#reconnect = options.reconnect;
    this.#reconnectionAttempts = options.reconnectionAttempts;

    if (storage[TOKEN_KEY]) {
      this.#connect();
    }
  }

  setStore = (store: Store) => {
    this.#store = store;
  };

  subscribe<S extends AllSubscriptions, E = ResponseDataEvent<S>>(
    { subscription, onMessage, onError }: Subscription<S, E>,
    previousSubId?: string,
  ): string {
    if (!subscription?.eventType || !onMessage) {
      throw new Error("Must provide all subscription options to subscribe.");
    }

    const subId = previousSubId ?? nanoid();
    const message = {
      ...subscription,
      messageType: SubscriptionMessageTypeEnum.Start,
      id: subId,
      token: storage[TOKEN_KEY],
    } as SubscriptionData<S>;

    this.#sendMessage(message);
    this.#subscriptions[subId] = {
      subscription,
      onMessage,
      onError,
    } as Subscription<AllSubscriptions>;

    this.#waitingSubscriptions[subId] = true;

    setTimeout(() => {
      if (this.#waitingSubscriptions[subId]) {
        this.unsubscribe(subId);
        onError?.(new Error("Subscription timed out - no response from server"));
      }
    }, this.#subscriptionTimeout);

    return subId;
  }

  unsubscribe(subId: string): void {
    delete this.#subscriptions[subId];
    delete this.#waitingSubscriptions[subId];

    const message: UnSubscriptionData = {
      messageType: SubscriptionMessageTypeEnum.End,
      id: subId,
      token: storage[TOKEN_KEY],
    };

    this.#sendMessage(message);
  }

  #unsubscribeAll(): void {
    for (const subId in this.#subscriptions) {
      this.unsubscribe(subId);
    }
  }

  #sendMessage(message: SubscriptionData<AllSubscriptions> | UnSubscriptionData) {
    if (!this.#client) throw new Error("Client is not connected to a websocket.");

    switch (this.#client.readyState) {
      case this.#client.OPEN:
        this.#client.send(JSON.stringify(message));
        break;
      case this.#client.CONNECTING:
        this.#unsentMessagesQueue.push(message);
        break;
      default:
        if (!this.#reconnecting) {
          throw new Error("Client is not connected to a websocket.");
        }
    }
  }

  #formatErrors(errors: Error | Error[]): Error {
    if (Array.isArray(errors)) {
      return errors[0];
    }

    if (errors?.message) {
      return errors;
    }

    return new Error("Unknown error");
  }

  closeAndNoReconnecting() {
    this.#reconnect = false;
    this.#unsubscribeAll();
    this.#reconnectSubscriptions = {};
    this.#client?.close();
  }

  connectAndKeepReconnecting() {
    this.#reconnect = true;
    this.#connect();
  }

  #tryReconnect() {
    if (!this.#reconnect) {
      return;
    }

    // try reconnecting forever for -1
    if (this.#reconnectionAttempts > -1 && this.#backoff.attempts > this.#reconnectionAttempts) {
      return;
    }

    if (!this.#reconnecting) {
      this.#reconnectSubscriptions = this.#subscriptions;
      this.#subscriptions = {};
      this.#waitingSubscriptions = {};
      this.#reconnecting = true;
    }

    const delay = this.#backoff.duration();

    setTimeout(() => {
      this.#connect();
    }, delay);
  }

  #connect() {
    const client = new WebSocket(this.#url);

    client.addEventListener("open", () => {
      if (this.#reconnecting) {
        this.#store?.dispatch(reconnectedWebsocket() as unknown as AnyAction);
      } else {
        this.#store?.dispatch(connectedWebsocket() as unknown as AnyAction);
      }

      this.#reconnecting = false;
      this.#backoff.reset();

      for (const key in this.#reconnectSubscriptions) {
        if (import.meta.env.DEV || import.meta.env.MODE === "staging") {
          console.warn("Try to subscribe to reconnectSubscriptions for key '%s'", key);
        }

        this.subscribe(this.#reconnectSubscriptions[key], key);
      }

      for (const message of this.#unsentMessagesQueue) {
        this.#client?.send(JSON.stringify(message));
      }

      this.#unsentMessagesQueue = [];
    });

    // see https://www.greenbytes.de/tech/webdav/rfc6455.html#closestatus
    client.addEventListener("close", () => {
      this.#store?.dispatch(disconnectedWebsocket() as unknown as AnyAction);

      if (storage[TOKEN_KEY]) {
        this.#tryReconnect();
      }
    });

    client.addEventListener("message", (event: MessageEvent<string>) => {
      let parsedMessage: ResponseData<AllSubscriptions>;

      try {
        parsedMessage = JSON.parse(event.data);
      } catch (e) {
        throw new Error("Message must be JSON-parsable.");
      }

      const subId = parsedMessage.id;

      if (parsedMessage.messageType !== SubscriptionMessageTypeEnum.KeepAlive && !this.#subscriptions[subId]) {
        this.unsubscribe(subId);
        return;
      }

      switch (parsedMessage.messageType) {
        case SubscriptionMessageTypeEnum.Success:
          delete this.#waitingSubscriptions[subId];
          break;
        case SubscriptionMessageTypeEnum.Fail:
          {
            const errorHandler = this.#subscriptions[subId].onError;
            delete this.#subscriptions[subId];
            delete this.#waitingSubscriptions[subId];
            errorHandler?.(this.#formatErrors(parsedMessage.errors));
          }
          break;
        case SubscriptionMessageTypeEnum.Data:
          if (parsedMessage.event && !parsedMessage.errors) {
            this.#subscriptions[subId].onMessage(parsedMessage.event, parsedMessage.id);
          } else {
            this.#subscriptions[subId].onError?.(this.#formatErrors(parsedMessage.errors));
          }
          break;
        case SubscriptionMessageTypeEnum.KeepAlive:
          break;
        default:
          throw new Error(
            "Invalid message type - must be of type `subscription_start`, `subscription_data` or `subscription_keepalive`.",
          );
      }
    });

    this.#client = client;
  }
}
